import { Form } from 'components/Wizards/Form/Form'
import { Formik, useFormikContext } from 'formik'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { BrandKitField } from 'components/Wizards/Wizard/Form/Design/BrandKitField/BrandKitField'
import { BrandVoiceField } from 'components/Wizards/Wizard/Form/Design/BrandVoiceField/BrandVoiceField'
import Styles from './RefineCaption.module.scss'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { SourceMaterialField } from 'components/Wizards/Wizard/Form/Design/SourceMaterialField/SourceMaterialField'
import { Button } from 'blaze-ui/Button/Button'
import { ArrowRight, ImageAdd } from 'blaze-ui/icons'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Heading } from 'blaze-ui/Heading/Heading'
import { DesktopTopic } from '../PurposeAndGoals/DesktopTopic'
import { useSections } from 'components/Wizards/Wizard/Form/Sections/SectionsProvider'
import { CollapsibleSection } from 'components/Wizards/Wizard/Form/CollapsibleSection/CollapsibleSection'
import { Field } from 'components/Wizards/Wizard/Form/Field'
import { AdditionalFields } from 'components/Wizards/Wizard/Form/Sections/AutoFillFieldsSection'
import { Sections } from 'components/Wizards/Wizard/Form/Sections'
import { FilePathFormikField } from 'components/Wizards/FilePathFormikField/FilePathFormikField'
import { useState } from 'react'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { CaptionType, MultiStepSchema } from 'components/Wizards/schemas/Types'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { capitalize } from 'lodash'
import { Item } from '@react-stately/collections'
import { FormSectionContentProps, RefineCaptionProps, Values } from './Types'
import { ConditionalInlineLanguageInputField as InlineLanguageInputField } from '../../../../Wizard/Form/LanguageInputField/ConditionalLanguageInputField'

const FormSectionContent = multiStep<StepProps & FormSectionContentProps>(({ schema, isLoading, setIsLoading }) => {
  const isBrandKitsEnabled = useFlipperFlag('brand_kits')
  const { collapsedFields, additionalFields } = useSections()
  const { setFieldValue, values } = useFormikContext()
  const [captionType, setCaptionType] = useState<CaptionType>((values as Values).caption_type)
  const wizardSchema = useWizardSchema() as MultiStepSchema

  const fieldsWithName = (names: string[]) => Object.entries(schema.fields).filter(([name]) => names.includes(name))
  const topicField = fieldsWithName(['topic'])[0]
  const filenameField = fieldsWithName(['filename'])[0]
  const captionTypeField = fieldsWithName(['caption_type'])[0]
  const captionTypes = wizardSchema.meta.captionTypes || {}

  return (
    <Form.Section>
      <div className={Styles.container}>
        <Heading level={1}> {schema.describe().meta.header}</Heading>

        <Form.Fields className={Styles.formFields}>
          {Object.keys(captionTypes).length > 0 && (
            <BaseField label={captionTypeField[1].describe().meta.label}>
              <div className={Styles.segmentedControlContainer}>
                <SegmentedControl
                  onItemClick={(value) => {
                    setFieldValue('caption_type', value)
                    setCaptionType(value as CaptionType)
                  }}
                  activeItem={captionType}
                  className={Styles.toggleElement}
                  isDisabled={isLoading}
                  variant="secondary"
                  rounded
                  size="md"
                  items={Object.keys(captionTypes).map((type) => ({
                    id: type,
                    name: capitalize(type),
                  }))}
                >
                  {(item) => <Item>{item.name}</Item>}
                </SegmentedControl>
              </div>
            </BaseField>
          )}

          {topicField && (
            <DesktopTopic topicSchema={topicField[1]} onLoadingChange={setIsLoading} fields={['topic', 'filename']} />
          )}

          {!isBrandKitsEnabled && (
            <BaseField
              name="design_wizard_fields"
              label="Select source materials, brand voice and style"
              direction="column"
            >
              <div className={Styles.fieldsContainer}>
                <BrandVoiceField />
                <BrandKitField />
                <SourceMaterialField />
              </div>
            </BaseField>
          )}

          {filenameField && (
            <BaseField label={filenameField[1].describe().meta.label} isRequired>
              <FilePathFormikField
                isNameLoading={isLoading}
                placeholder={filenameField[1].describe().meta.placeholder}
                triggerComponent="button"
                inputClassName={Styles.filenameInput}
              />
            </BaseField>
          )}

          {(collapsedFields.length > 0 || additionalFields.length > 0) && (
            <CollapsibleSection>
              {collapsedFields.map(([name, schema], i) => (
                <Field key={name} name={name} schema={schema} autoFocus={i === 0} />
              ))}

              <AdditionalFields fields={additionalFields} hideLabel={!collapsedFields.length} />
            </CollapsibleSection>
          )}
        </Form.Fields>
      </div>
    </Form.Section>
  )
})

export const RefineCaption = multiStep<StepProps & RefineCaptionProps>((props) => {
  const [isLoading, setIsLoading] = useState(false)
  const { schema, initialValues, onSubmit, onBack, onShowImages } = props
  const {
    meta: { submitLabel, previousLabel },
  } = schema.describe()

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
      enableReinitialize
    >
      {({ isSubmitting, submitForm }) => {
        return (
          <Form.Root>
            <Sections.Root schema={schema}>
              <FormSectionContent {...props} isLoading={isLoading} setIsLoading={setIsLoading} />
            </Sections.Root>

            <Form.Actions isSticky>
              <Form.PreviousButton onPress={onBack}>{previousLabel}</Form.PreviousButton>
              <Form.PrimaryActions>
                <InlineLanguageInputField />

                {onShowImages && (
                  <Button
                    variant="tertiary"
                    size="lg"
                    frontIcon={ImageAdd}
                    isDisabled={isLoading || isSubmitting}
                    onPress={() => {
                      onShowImages?.(true)
                      submitForm()
                    }}
                  >
                    Add Media
                  </Button>
                )}
                <Button size="lg" type="submit" isDisabled={isSubmitting} endIcon={ArrowRight} onPress={submitForm}>
                  {submitLabel}
                </Button>
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )
      }}
    </Formik>
  )
})
